import i18n from "@/i18n/i18n";
import { AuthorizationService } from "@/shared/services/authorizationService";
import axios, {
	AxiosHeaders,
	AxiosInstance,
	AxiosRequestConfig,
	AxiosResponse,
} from "axios";
import { ItiString } from "../utils/iti-string";
import { ItiLoadingService } from "./iti-loading-service";
import { ItiMessageService } from "./iti-message-service";
import { ItiTokenService } from "./iti-token-service";

export const authHeader = () => {
	return ItiTokenService.hasToken()
		? `Bearer ${ItiTokenService.getToken()}`
		: ItiString.Empty;
};

let refreshTokenRequest: Promise<boolean> | null = null;
const tryRenewToken = async () => {
	if (!refreshTokenRequest) {
		refreshTokenRequest = AuthorizationService.refreshToken();
		refreshTokenRequest.then((res: boolean) => {
			if (!res && signOutUser) {
				signOutUser();
			}
			refreshTokenRequest = null;
		});
	}
};

const processResponse = (
	response: AxiosResponse,
	showLoading: boolean,
	fullResponse = false,
) => {
	if (showLoading) {
		ItiLoadingService.disableLoading();
	}
	return fullResponse ? response : response.data;
};

const processError = (
	error: any,
	showLoading: boolean,
	showMessageError: boolean,
) => {
	if (showLoading) {
		ItiLoadingService.disableLoading();
	}

	if (showMessageError) {
		const message = getErrorMessageFromResponse(error);

		if (!ItiString.isNullOrWhiteSpace(message)) {
			ItiMessageService.toastError(message);
		}
	}
	throw error;
};

const getErrorMessageFromResponse = (error: any): string => {
	if (error.message === "Network Error") {
		return i18n.t("errors.404");
	}

	return composeError(error);
};

const composeError = (error: any): string => {
	const errors = error?.response?.data?.errors;
	const fieldErrors = error?.response?.data?.fieldErrors;

	const errorMessages = [];

	if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
		for (const err of fieldErrors) {
			if (err?.message) {
				errorMessages.push({
					message: err.message,
					type: "error",
				});
			}
		}
	} else if (Array.isArray(errors) && errors.length > 0) {
		for (const err of errors) {
			if (err?.message) {
				errorMessages.push({ message: err.message, type: "error" });
			}
		}
	}

	if (errorMessages.length > 0) {
		ItiMessageService.toastNotificationsMultiple(errorMessages);
		return "";
	}

	return (
		(error.response &&
			(error.response.data.message ||
				(error.response.data && i18n.t(error.response.data.Message)) ||
				(error.response.status &&
					getStatusTextFromCode(error.response.status)) ||
				error.response.statusText ||
				(error.response.statusCode &&
					getStatusTextFromCode(error.response.statusCode)))) ||
		error.Message ||
		error.message
	);
};

const getStatusTextFromCode = (status: any) => {
	return i18n.exists(`errors.${status}`)
		? i18n.t(`errors.${status}`)
		: "Unavailable Code";
};

let showErrorMessage = false;
const axiosInstance: AxiosInstance = axios.create({
	headers: {
		"Content-Type": "application/json",
		"Accept-Language": i18n.language,
	},
});
const axiosInstanceWithoutAuth: AxiosInstance = axios.create({
	headers: {
		"Content-Type": "application/json",
		"Accept-Language": i18n.language,
	},
});

let signOutUser: (() => void) | null = null;
let isMultipart = false;

export const ItiHttpService = {
	// Método para hacer una petición 'GET' al API
	//   - url: la url completa
	//   - params: parámetros
	//   - fullResponse: por defecto se devuelve solo el 'data' resultado de la petición,
	//                   pero se puede indicar false para obtener la Response entera
	//   - showMessageError: muestra un toast con el error

	get(
		url: string,
		params?: any,
		showLoading = true,
		fullResponse = false,
		showError: boolean = showErrorMessage,
		config?: AxiosRequestConfig, // opcional para pasar configuración adicional
	) {
		this.enableLoading(showLoading);

		const axiosConfig: AxiosRequestConfig = {
			...(config || {}),
			params: params,
		};

		return axiosInstance
			.get(url, axiosConfig)
			.then((res) => processResponse(res, showLoading, fullResponse))
			.catch((res) => processError(res, showLoading, showError));
	},

	// Método para hacer una petición 'GET' al API sin autenticación
	// (ver la explicación de los parámetros en el método 'get')
	getWithoutAuth(
		url: string,
		params?: any,
		showLoading = true,
		fullResponse = false,
		showError: boolean = showErrorMessage,
	) {
		this.enableLoading(showLoading);

		return axiosInstanceWithoutAuth
			.get(url, params)
			.then((res) => processResponse(res, showLoading, fullResponse))
			.catch((res) => processError(res, showLoading, showError));
	},
	// Método para hacer una petición 'POST' al API
	// (ver la explicación de los parámetros en el método 'get')
	post(
		url: string,
		params: any,
		showLoading = true,
		fullResponse = false,
		showError: boolean = showErrorMessage,
		config?: AxiosRequestConfig, // opcional para pasar configuración adicional
	) {
		this.enableLoading(showLoading);

		return axiosInstance
			.post(url, params, config)
			.then((res) => processResponse(res, showLoading, fullResponse))
			.catch((res) => processError(res, showLoading, showError));
	},
	// Método para hacer una petición 'POST' al API sin autenticación
	// (ver la explicación de los parámetros en el método 'get')
	postWithoutAuth(
		url: string,
		params: any,
		showLoading = true,
		fullResponse = false,
		showError: boolean = showErrorMessage,
	) {
		this.enableLoading(showLoading);

		return axiosInstanceWithoutAuth
			.post(url, params)
			.then((res) => processResponse(res, showLoading, fullResponse))
			.catch((res) => processError(res, showLoading, showError));
	},
	// Método para hacer una petición 'PUT' al API
	// (ver la explicación de los parámetros en el método 'get')
	put(
		url: string,
		params: any,
		showLoading = true,
		fullResponse = false,
		showError: boolean = showErrorMessage,
		config?: AxiosRequestConfig, // opcional para pasar configuración adicional
	) {
		this.enableLoading(showLoading);

		return axiosInstance
			.put(url, params, config)
			.then((res) => processResponse(res, showLoading, fullResponse))
			.catch((res) => processError(res, showLoading, showError));
	},
	// Método para hacer una petición 'PUT' al API sin autenticación
	// (ver la explicación de los parámetros en el método 'put')
	putWithoutAuth(
		url: string,
		params: any,
		showLoading = true,
		fullResponse = false,
		showError: boolean = showErrorMessage,
	) {
		this.enableLoading(showLoading);

		return axiosInstanceWithoutAuth
			.put(url, params)
			.then((res) => processResponse(res, showLoading, fullResponse))
			.catch((res) => processError(res, showLoading, showError));
	},
	// Método para hacer una petición 'DELETE' al API
	// (ver la explicación de los parámetros en el método 'get')
	delete(
		url: string,
		params?: any,
		showLoading = true,
		fullResponse = false,
		showError: boolean = showErrorMessage,
	) {
		this.enableLoading(showLoading);
		return axiosInstance
			.delete(url, { data: params })
			.then((res) => processResponse(res, showLoading, fullResponse))
			.catch((res) => processError(res, showLoading, showError));
	},
	// Método para hacer una petición 'PATCH' al API
	// (ver la explicación de los parámetros en el método 'get')
	patch(
		url: string,
		params: any,
		showLoading = true,
		fullResponse = false,
		showError: boolean = showErrorMessage,
	) {
		this.enableLoading(showLoading);

		return axiosInstance
			.patch(url, params)
			.then((res) => processResponse(res, showLoading, fullResponse))
			.catch((res) => processError(res, showLoading, showError));
	},
	// Método para recuperar la instancia de axios
	// usado por jest en testing
	getAxiosInstance(): AxiosInstance {
		return axiosInstance;
	},
	enableLoading(showLoading: boolean) {
		if (showLoading) {
			ItiLoadingService.enableLoading();
		}
	},
	enableShowErrorMessage(value: boolean) {
		showErrorMessage = value;
	},
	composeAuthHeader() {
		axiosInstance.defaults.headers["Content-Type"] = "application/json";
		axiosInstance.defaults.headers.common.Authorization = authHeader();
	},
	setInterceptor(signOut: () => void) {
		signOutUser = signOut;
		axiosInstance.interceptors.response.use(
			(response) => {
				//Dispatch any action on success
				return response;
			},
			(error) => {
				// if (error.response.status === 401 || error.response.status === 403) {
				if (error.response.status === 401) {
					signOut();
				}
				return Promise.reject(error);
			},
		);

		axiosInstance.interceptors.request.use((config) => {
			if (ItiTokenService.isTokenExpired()) {
				tryRenewToken();
			}
			const token = ItiTokenService.getToken();
			config.headers = { ...config.headers } as AxiosHeaders;
			config.headers.Authorization = token ? `Bearer ${token}` : "";

			config.headers["Content-Type"] = isMultipart
				? "multipart/form-data"
				: "application/json";
			return config;
		});
	},
	// Método para hacer una petición 'POST' con subida de fichero (multipart) al API
	// (ver la explicación de los parámetros en el método 'get')
	postMultiPart(
		url: string,
		params: any,
		showLoading = true,
		fullResponse = false,
		showError: boolean = showErrorMessage,
	) {
		this.enableLoading(showLoading);
		isMultipart = true;

		return axiosInstance
			.post(url, params)
			.then((res) => {
				isMultipart = false;
				return processResponse(res, showLoading, fullResponse);
			})
			.catch((res) => {
				isMultipart = false;
				return processError(res, showLoading, showError);
			});
	},
	postxWwwFormUrlencoded(
		url: string,
		params: URLSearchParams,
		showLoading = true,
		fullResponse = false,
		showError: boolean = showErrorMessage,
	) {
		this.enableLoading(showLoading);

		const axiosInstance = axios.create({
			headers: {
				Accept: "application/json",
				"Content-Type": "application/x-www-form-urlencoded",
				Authorization: authHeader(), //Se usa para el refresh token, ya que necesita el token
			},
		});

		return axiosInstance
			.post(url, params)
			.then((res) => processResponse(res, showLoading, fullResponse))
			.catch((res) => processError(res, showLoading, showError));
	},
};
